import { FC, PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { ThemeProvider } from "styled-components";

import { useHideItem } from "@/App/hooks";

import { getExperienceAuthenticated, getExperienceUnauthenticated } from "../api/experience";
import { getViewRequest } from "../api/request";
import { SearchParamKeys, nonRedirectableAuthnzPaths } from "../constants/constants";
import { AppExperienceData } from "../types/experience";
import { AuthenticationContext } from "./AuthenticationContext";
import { MemberEligibilityContext } from "./MemberEligibilityContext";

export const ExperienceContext = createContext<{
  readonly experience: AppExperienceData;
  readonly isLoadingExperience: boolean;
  readonly historicalClaimsMessage?: string;
  readonly priorAuthorizationMessage?: string;
  readonly priorAuthorizationPhoneNumber?: string;
  readonly formularyLookupOverrideUrl?: string;
  readonly loginRedirectUrl?: string;
  readonly onlinePharmacyIdentifier?: string;
  readonly mailPharmacyIdentifier?: string;
  readonly hideDashboard?: boolean;
  readonly hidePlanContributions?: boolean;
  readonly hidePreferences?: boolean;
  readonly hidePriorAuthorization?: boolean;
  readonly hideEligibility?: boolean;
  readonly hideFormularyLookup?: boolean;
  readonly hideMemberClaimsPage?: boolean;
  readonly hideFindBestPricePage?: boolean;
  readonly hideOnlinePharmacyOnFBPTool?: boolean;
  readonly hideMailPharmacyOnFBPTool?: boolean;
  readonly hideMemberCard?: boolean;
  readonly hideInboxPage?: boolean;
  readonly hideMemberRegistration?: boolean;
  readonly hideCustomerCarePhoneNumber?: boolean;
  readonly hideDirectMemberReimbursement?: boolean;
  readonly hideHomeDeliveryCard?: boolean;
  readonly hideSpecialtyCard?: boolean;
  readonly hideCostcoHomeDeliveryCard?: boolean;
  readonly hideCostcoSpecialtyCard?: boolean;
  readonly hideAccumulators?: boolean;
  readonly hideNearbyPharmaciesPage?: boolean;
  readonly hideFormDocumentsPage?: boolean;
  readonly hideMedicarePrescriptionPaymentPlan?: boolean;
  readonly hideJudiCareOutboundSSO?: boolean;
  readonly hideMedicareMaterialId?: boolean;
  readonly dashboardVersion?: string;
  readonly logoDesktopUrl?: string;
  readonly logoMobileUrl?: string;
  readonly urlIdentifier?: string;
  readonly urlIdentifierType?: string;
  readonly medicarePrescriptionPaymentPlanOnlineSection?: string;
  readonly medicarePrescriptionPaymentPlanPhoneSection?: string;
  readonly medicarePrescriptionPaymentPlanEmailSection?: string;
  readonly medicarePrescriptionPaymentPlanMailSection?: string;
  readonly medicarePrescriptionPaymentPlanExplanationSection?: string;
  readonly overrideLandingPage?: string;
  readonly customerSupportHours?: string;
  readonly customerSupportPhoneNumber?: string;
  readonly memberCardCustomerSupportHours?: string;
  readonly medicareMaterialId?: string;
} | null>(null);

export const ExperienceProvider: FC<PropsWithChildren> = ({ children }): JSX.Element => {
  const { authState, getAuthToken } = useContext(AuthenticationContext);
  const [experience, setExperience] = useState<AppExperienceData>(undefined);
  const [isLoadingExperience, setIsLoadingExperience] = useState<boolean>(true);
  const [hasFetchedExperience, setHasFetchedExperience] = useState<boolean>(false);
  const [hasCheckedAuthnzRoutes, setHasCheckedAuthnzRoutes] = useState(false);
  const [currentMemberEligId, setCurrentMemberEligId] = useState(0);
  const [experienceFetchedFrom, setExperienceFetchedFrom] = useState<"url" | "eligibility">(
    authState === "signedIn" ? "eligibility" : "url",
  );
  const [currentLanguage, setCurrentLanguage] = useState("");
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const hideFormularyLookup = useHideItem("hide_formulary_lookup", experience);
  const hideMemberClaimsPage = useHideItem("hide_member_claims", experience);
  const hideFindBestPricePage = useHideItem("hide_member_fbp_tool", experience);
  const hideOnlinePharmacyOnFBPTool = useHideItem("hide_online_pharmacy_on_fbp_tool", experience);
  const hideMailPharmacyOnFBPTool = useHideItem("hide_mail_pharmacy_on_fbp_tool", experience);
  const hideMemberCard = useHideItem("hide_member_card", experience);
  const hideInboxPage = useHideItem("hide_inbox_page", experience);
  const hideMemberRegistration = useHideItem("hide_member_registration", experience);
  const hideCustomerCarePhoneNumber = useHideItem("hide_customer_care_phone_number", experience);
  const hideDirectMemberReimbursement = useHideItem("hide_direct_member_reimbursement", experience);
  const hideHomeDeliveryCard = useHideItem("hide_home_delivery_card", experience);
  const hideSpecialtyCard = useHideItem("hide_specialty_card", experience);
  const hideCostcoHomeDeliveryCard = useHideItem("hide_costco_home_delivery_card", experience);
  const hideCostcoSpecialtyCard = useHideItem("hide_costco_specialty_card", experience);
  const hideAccumulators = useHideItem("hide_accumulators", experience);
  const hideNearbyPharmaciesPage = useHideItem("hide_nearby_pharmacies_page", experience);
  const hideFormDocumentsPage = useHideItem("hide_form_documents_page", experience);
  const hideMedicarePrescriptionPaymentPlan = useHideItem("hide_medicare_prescription_payment_plan", experience);
  const hideDashboard = useHideItem("hide_dashboard", experience);
  const hidePlanContributions = useHideItem("hide_plan_contributions", experience);
  const hidePreferences = useHideItem("hide_preferences", experience);
  const hidePriorAuthorization = useHideItem("hide_prior_authorization", experience);
  const hideEligibility = useHideItem("hide_eligibility", experience);
  const hideJudiCareOutboundSSO = useHideItem("hide_judi_care_outbound_sso", experience);
  const hideMedicareMaterialId = useHideItem("hide_medicare_material_id", experience);
  const { i18n } = useTranslation();

  const dashboardVersion: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const control = uxControls.find((c) => c.control === "dashboard_version");
    return control?.value;
  }, [experience]);

  const historicalClaimsMessage: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const historicalClaimsMessageControl = uxControls.find((c) => c.control === "historical_claims_message");
    return historicalClaimsMessageControl?.value;
  }, [experience]);

  const priorAuthorizationMessage: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const priorAuthorizationMessageControl = uxControls.find((c) => c.control === "prior_authorization_message");
    return priorAuthorizationMessageControl?.value;
  }, [experience]);

  const priorAuthorizationPhoneNumber: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const priorAuthorizationPhoneNumberControl = uxControls.find((c) => c.control === "prior_authorization_phone_number");
    return priorAuthorizationPhoneNumberControl?.value;
  }, [experience]);

  const formularyLookupOverrideUrl: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const control = uxControls.find((c) => c.control === "override_formulary_lookup_url");
    return control?.value;
  }, [experience]);

  const loginRedirectUrl: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const loginRedirectControl = uxControls.find((c) => c.control === "login_redirect");
    return loginRedirectControl?.value;
  }, [experience]);

  const onlinePharmacyIdentifier: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const onlinePharmacyIdentifierControl = uxControls.find((c) => c.control === "online_pharmacy_identifier");
    return onlinePharmacyIdentifierControl?.value;
  }, [experience]);

  const mailPharmacyIdentifier: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const mailPharmacyIdentifierControl = uxControls.find((c) => c.control === "mail_pharmacy_identifier");
    return mailPharmacyIdentifierControl?.value;
  }, [experience]);

  const medicarePrescriptionPaymentPlanOnlineSection: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const mpppUrlControl = uxControls.find((c) => c.control === "medicare_prescription_payment_plan_online_section");
    return mpppUrlControl?.value;
  }, [experience]);

  const medicarePrescriptionPaymentPlanPhoneSection: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const mpppPhoneNumberControl = uxControls.find((c) => c.control === "medicare_prescription_payment_plan_phone_section");
    return mpppPhoneNumberControl?.value;
  }, [experience]);

  const medicarePrescriptionPaymentPlanMailSection: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const mpppPhoneNumberControl = uxControls.find((c) => c.control === "medicare_prescription_payment_plan_mail_section");
    return mpppPhoneNumberControl?.value;
  }, [experience]);

  const medicarePrescriptionPaymentPlanEmailSection: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const mpppPhoneNumberControl = uxControls.find((c) => c.control === "medicare_prescription_payment_plan_email_section");
    return mpppPhoneNumberControl?.value;
  }, [experience]);

  const medicarePrescriptionPaymentPlanExplanationSection: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const mpppPhoneNumberControl = uxControls.find((c) => c.control === "medicare_prescription_payment_plan_explanation_section");
    return mpppPhoneNumberControl?.value;
  }, [experience]);

  const overrideLandingPage: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const defaultRoute = uxControls.find((c) => c.control === "override_landing_page");
    return defaultRoute?.value;
  }, [experience]);

  const logoDesktopUrl: string = useMemo(() => {
    return experience?.theme?.logo_desktop;
  }, [experience]);

  const logoMobileUrl: string = useMemo(() => {
    return experience?.theme?.logo_mobile || experience?.theme?.logo_desktop;
  }, [experience]);

  const urlIdentifier: string = useMemo(() => {
    return experience?.identifier;
  }, [experience]);

  const urlIdentifierType: "query" | "subdomain" = useMemo(() => {
    return experience?.identifier_type;
  }, [experience]);

  const customerSupportPhoneNumber: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const customerSupportPhoneNumber = uxControls.find((c) => c.control === "customer_support_phone_number");
    return customerSupportPhoneNumber?.value;
  }, [experience]);

  const customerSupportHours: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const customerSupportHours = uxControls.find((c) => c.control === "customer_support_hours");
    return customerSupportHours?.value;
  }, [experience]);

  const memberCardCustomerSupportHours: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const memberCardCustomerSupportHours = uxControls.find((c) => c.control === "member_card_customer_support_hours");
    return memberCardCustomerSupportHours?.value;
  }, [experience]);

  const medicareMaterialId: string | undefined = useMemo(() => {
    const uxControls = experience?.ux_controls || [];
    const medicareMaterialId = uxControls.find((c) => c.control === "medicare_material_id");
    return medicareMaterialId?.value;
  }, [experience]);

  useEffect(() => {
    if ((memberEligibility?.member?.id || 0) !== 0) {
      if (memberEligibility.member.id !== currentMemberEligId || i18n.language !== currentLanguage) {
        setHasFetchedExperience(false);
        setIsLoadingExperience(true);
        setExperienceFetchedFrom("eligibility");
        checkEligibilityBasedExperience(i18n.language).then(() => {
          setHasFetchedExperience(true);
        });
      }
    } else if (authState !== "signedIn") {
      setHasFetchedExperience(false);
      setIsLoadingExperience(true);
      setExperienceFetchedFrom("url");
      checkUrlBasedExperience(i18n.language).then(() => {
        setHasFetchedExperience(true);
      });
    } else {
      setHasFetchedExperience(true);
      setIsLoadingExperience(false);
    }
  }, [memberEligibility?.member?.id, i18n?.language, currentMemberEligId, currentLanguage, authState]);

  const checkEligibilityBasedExperience = async (language: string) => {
    setCurrentMemberEligId(memberEligibility.member.id);
    setCurrentLanguage(language);
    const asOfDate = searchParams.get(SearchParamKeys.EXPERIENCE_AS_OF_DATE);

    try {
      let body = {
        asOfDate: asOfDate,
        language: language,
      };
      if (isViewingDataholder) {
        body = { ...body, ...getViewRequest(memberEligibility) };
      }
      // @ts-ignore
      const experienceGetResponse = await getExperienceAuthenticated(await getAuthToken(), body);
      setExperience({
        identifier: experienceGetResponse.identifier,
        identifier_type: experienceGetResponse.identifier_type,
        theme: experienceGetResponse.theme,
        ux_controls: experienceGetResponse.ux_controls,
        menu_items: experienceGetResponse.menu_items,
      });
    } catch {
      setExperience(undefined);
    }
  };

  const checkUrlBasedExperience = async (language: string) => {
    const host = window.location.host;
    const clientSubdomain = host.split(".")[0];
    const clientName = searchParams.get("client");
    const asOfDate = searchParams.get("asOfDate");

    try {
      setCurrentLanguage(language);
      const experienceGetResponse = await getExperienceUnauthenticated({
        identifier: clientName,
        subdomain: clientSubdomain,
        as_of_date: asOfDate,
        language: language,
      });
      setExperience({
        identifier: experienceGetResponse.identifier,
        identifier_type: experienceGetResponse.identifier_type,
        theme: experienceGetResponse.theme,
        ux_controls: experienceGetResponse.ux_controls,
      } as AppExperienceData);
    } catch {
      setExperience(undefined);
    }
  };

  /**
   * this is still jank, someone please save us from ourselves.
   */
  useEffect(() => {
    const willLoginRedirect =
      loginRedirectUrl
      && authState === "signedOut"
      && (!nonRedirectableAuthnzPaths.includes(location.pathname) || location.pathname == "/login"); // allow login redirect when signed out on the login page tho

    if (hasCheckedAuthnzRoutes && hasFetchedExperience && !willLoginRedirect) {
      const faviconUrl = experience?.theme?.favicon ?? `${site.corporateThemeUrl}/web/capitalrx/favicon.png`;
      // set the favicon
      const faviconLinkElement: HTMLAnchorElement = document.querySelector("link[rel~='icon']");
      faviconLinkElement.href = faviconUrl;
      setIsLoadingExperience(false);
    }

    if (willLoginRedirect) {
      window.location.href = loginRedirectUrl;
    } else {
      setHasCheckedAuthnzRoutes(true);
    }

    if (authState === "signedOut") {
      setCurrentMemberEligId(0);
    }
  }, [experience, hasCheckedAuthnzRoutes, hasFetchedExperience, authState]);

  // handle setting the client query param
  useEffect(() => {
    if (experienceFetchedFrom === "eligibility") {
      if (urlIdentifier) {
        if (urlIdentifierType === "query") {
          setSearchParams((prev: URLSearchParams) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const newParams: any = {};
            for (const entry of prev.entries()) {
              newParams[entry[0]] = entry[1];
            }
            newParams["client"] = urlIdentifier;
            return newParams;
          });
        }
        // TODO -- do we need to handle the subdomain?
      } else {
        if (searchParams.get("client")) {
          setSearchParams((prev) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const newParams: any = {};
            for (const entry of prev.entries()) {
              newParams[entry[0]] = entry[1];
            }
            if (newParams["client"]) {
              delete newParams["client"];
            }
            return newParams;
          });
        }
      }
    }
  }, [urlIdentifier, urlIdentifierType, experienceFetchedFrom]);

  const contextualIsLoading = useMemo(() => {
    if (experienceFetchedFrom === "url" && (memberEligibility?.member?.id || 0) !== 0) {
      return true;
    }
    return !hasFetchedExperience || isLoadingExperience || !hasCheckedAuthnzRoutes;
  }, [hasCheckedAuthnzRoutes, hasFetchedExperience, isLoadingExperience, memberEligibility, experienceFetchedFrom]);

  const isLoadingFromURL = useMemo(() => {
    return experienceFetchedFrom === "url" && isLoadingExperience;
  }, [isLoadingExperience, experienceFetchedFrom]);

  return (
    <ExperienceContext.Provider
      value={{
        experience: experience,
        isLoadingExperience: contextualIsLoading,
        historicalClaimsMessage: historicalClaimsMessage,
        priorAuthorizationMessage: priorAuthorizationMessage,
        priorAuthorizationPhoneNumber: priorAuthorizationPhoneNumber,
        formularyLookupOverrideUrl: formularyLookupOverrideUrl,
        loginRedirectUrl: loginRedirectUrl,
        onlinePharmacyIdentifier: onlinePharmacyIdentifier,
        mailPharmacyIdentifier: mailPharmacyIdentifier,
        hideFormularyLookup: hideFormularyLookup,
        hideMemberClaimsPage: hideMemberClaimsPage,
        hideFindBestPricePage: hideFindBestPricePage,
        hideOnlinePharmacyOnFBPTool: hideOnlinePharmacyOnFBPTool,
        hideMailPharmacyOnFBPTool: hideMailPharmacyOnFBPTool,
        hideMemberCard: hideMemberCard,
        hideInboxPage: hideInboxPage,
        hideMemberRegistration: hideMemberRegistration,
        hideCustomerCarePhoneNumber: hideCustomerCarePhoneNumber,
        hideDirectMemberReimbursement: hideDirectMemberReimbursement,
        hideHomeDeliveryCard: hideHomeDeliveryCard,
        hideSpecialtyCard: hideSpecialtyCard,
        hideCostcoHomeDeliveryCard: hideCostcoHomeDeliveryCard,
        hideCostcoSpecialtyCard: hideCostcoSpecialtyCard,
        hideAccumulators: hideAccumulators,
        hideNearbyPharmaciesPage: hideNearbyPharmaciesPage,
        hideFormDocumentsPage: hideFormDocumentsPage,
        hideMedicarePrescriptionPaymentPlan: hideMedicarePrescriptionPaymentPlan,
        hideDashboard: hideDashboard,
        hidePlanContributions: hidePlanContributions,
        hidePreferences: hidePreferences,
        hidePriorAuthorization: hidePriorAuthorization,
        hideEligibility: hideEligibility,
        hideJudiCareOutboundSSO: hideJudiCareOutboundSSO,
        hideMedicareMaterialId: hideMedicareMaterialId,
        dashboardVersion: dashboardVersion,
        medicarePrescriptionPaymentPlanOnlineSection: medicarePrescriptionPaymentPlanOnlineSection,
        medicarePrescriptionPaymentPlanMailSection: medicarePrescriptionPaymentPlanMailSection,
        medicarePrescriptionPaymentPlanEmailSection: medicarePrescriptionPaymentPlanEmailSection,
        medicarePrescriptionPaymentPlanPhoneSection: medicarePrescriptionPaymentPlanPhoneSection,
        medicarePrescriptionPaymentPlanExplanationSection: medicarePrescriptionPaymentPlanExplanationSection,
        overrideLandingPage: overrideLandingPage,
        logoDesktopUrl: logoDesktopUrl,
        logoMobileUrl: logoMobileUrl,
        urlIdentifier: urlIdentifier,
        urlIdentifierType: urlIdentifierType,
        customerSupportPhoneNumber: customerSupportPhoneNumber,
        customerSupportHours: customerSupportHours,
        memberCardCustomerSupportHours: memberCardCustomerSupportHours,
        medicareMaterialId: medicareMaterialId,
      }}
    >
      {<ThemeProvider theme={experience?.theme || {}}>{!isLoadingFromURL && children}</ThemeProvider>}
    </ExperienceContext.Provider>
  );
};
